import { type ReactNode } from "react";
import { FluentProvider, webLightTheme, webDarkTheme } from "@fluentui/react-components";
import { MsalAuthProvider } from "./auth/MsalProvider";
import { AuthStateProvider, useAuthState } from "./auth/AuthStateProvider";
import { SignInPage } from "./auth/SignInPage";
import { IdleSessionWarning } from "./auth/IdleSessionWarning";
import { TaskPane } from "./shared/components/TaskPane";
import { TelemetryErrorBoundary } from "./shared/components/TelemetryErrorBoundary";
import { hostToChannel } from "./hosts/channel";
import { hostToChannel as hostToSignInChannel } from "./shared/host";
import { AppConfigProvider, useAppConfig } from "./services/studio/AppConfigProvider";
import { ConnectionRegistryProvider } from "./services/ConnectionRegistryProvider";
import { ThemeProvider, useTheme } from "./shared/providers/ThemeProvider";
import { reloadAddin } from "./shared/utils/reloadAddin";

interface AppProps {
  readonly host: typeof Office.HostType[keyof typeof Office.HostType] | null;
}

function ConnectionRegistryFromConfig({ children }: { readonly children: ReactNode }) {
  const { data } = useAppConfig();
  return (
    <ConnectionRegistryProvider modules={data?.modules ?? []}>
      {children}
    </ConnectionRegistryProvider>
  );
}

function AppRouter({ host }: Readonly<AppProps>) {
  const { signedIn } = useAuthState();

  if (!signedIn) {
    return <SignInPage channel={hostToSignInChannel(host)} />;
  }

  return (
    <AppConfigProvider channel={hostToChannel(host)}>
      <ConnectionRegistryFromConfig>
        <TaskPane host={host} />
        {/* Sits alongside the pane rather than inside a module: the inactivity
            timeout ends the whole session, so the prompt has to be reachable
            from whichever module the user was last in. Renders nothing until
            the session is inside the warning window. */}
        <IdleSessionWarning />
      </ConnectionRegistryFromConfig>
    </AppConfigProvider>
  );
}

function ThemedFluentProvider({ children }: { readonly children: ReactNode }) {
  const { darkMode } = useTheme();
  return (
    <FluentProvider theme={darkMode ? webDarkTheme : webLightTheme}>
      {children}
    </FluentProvider>
  );
}

export default function App({ host }: Readonly<AppProps>) {
  return (
    <TelemetryErrorBoundary
      label="AiVA ran into an unexpected error. Reload to try again."
      actionLabel="Reload"
      onAction={reloadAddin}
    >
      <ThemeProvider>
        <ThemedFluentProvider>
          <MsalAuthProvider channel={hostToChannel(host)}>
            <AuthStateProvider>
              <AppRouter host={host} />
            </AuthStateProvider>
          </MsalAuthProvider>
        </ThemedFluentProvider>
      </ThemeProvider>
    </TelemetryErrorBoundary>
  );
}
